/**
 * Admin translations
 * Admin dashboard and management UI text
 */

import { LANGUAGES } from '../config';

export const adminTranslations = {
  [LANGUAGES.EN]: {
    dashboard: 'Admin Dashboard',
    users: 'Users',
    roles: 'Roles',
    settings: 'Settings',
    analytics: 'Analytics',
    logs: 'System Logs',
    maintenance: 'Maintenance',
    backup: 'Backup & Restore',
    userManagement: 'User Management',
    roleManagement: 'Role Management',
    role: 'Role',
    department: 'Department',
    departmentLabel: 'Department',
    departmentPlaceholder: 'Select Department',
    noDepartment: 'No Department',
    description: 'Description',
    permissions: 'Permissions',
    actions: 'Actions',
    permissionsCount: 'Permissions Count',
    roleLabels: {
      user: 'User',
      manager: 'Manager',
      admin: 'Admin'
    },
    createdBy: 'Created By',
    createdAt: 'Created At',
    used: 'Used',
    unused: 'Available',
    available: 'Available',
    emailAddress: 'Email Address',
    type: 'Type',
    createdOn: 'Created On',
    expiresOn: 'Expires On',
    pending: 'Pending',
    // Department Management
    departmentManagement: 'Department Management',

    // Skills Management
    skillManagement: 'Skills Management',
    skills: {
      title: 'Skill Categories Management',
      description: 'Manage skill categories used in CV analyzer and job analyzer. These categories help organize and categorize competencies and skills.',
      name: 'Name',
      descriptionLabel: 'Description',
      type: 'Type',
      order: 'Display Order',
      status: 'Status',
      active: 'Active',
      created: 'Created',
      noCategories: 'No skill categories found',
      addCategory: 'Add Category',
      editCategory: 'Edit Category',
      confirmDelete: 'Are you sure you want to delete this category?',
      loadError: 'Failed to load skill categories',
      saveError: 'Failed to save category',
      deleteError: 'Failed to delete category',
      createSuccess: 'Category created successfully',
      updateSuccess: 'Category updated successfully',
      deleteSuccess: 'Category deleted successfully',
      typeAll: 'All (CV & Job)',
      typeCv: 'CV Only',
      typeJob: 'Job Only'
    },

    departments: {
      title: 'Department Management',
      subtitle: 'Manage organizational departments and assignments',
      nameColumn: 'Department Name',
      descriptionColumn: 'Description',
      statusColumn: 'Status',
      usersColumn: 'Users',
      createdColumn: 'Created',
      addDepartment: 'Add Department',
      editDepartment: 'Edit Department',
      deleteDepartment: 'Delete Department',
      nameLabel: 'Department Name',
      nameRequired: 'Department name is required',
      nameAlreadyExists: 'Department name already exists',
      namePlaceholder: 'Enter department name',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Enter department description (optional)',
      createButton: 'Create Department',
      updateButton: 'Update Department',
      createSuccess: 'Department created successfully',
      updateSuccess: 'Department updated successfully',
      deleteSuccess: 'Department deleted successfully',
      createError: 'Failed to create department',
      updateError: 'Failed to update department',
      deleteError: 'Failed to delete department',
      submitError: 'An error occurred while processing your request',
      confirmDelete: 'Are you sure you want to delete "{name}"? This action cannot be undone.',
      usersInDepartment: 'users in this department'
    },
    // System Language Management
    systemLanguageManagement: 'System Language Management',
    reprocessingManagement: 'Reprocessing',
    reprocessing: {
      title: 'Analyzer Reprocessing',
      description: 'Replay saved combined inputs after prompt, taxonomy, or company changes without re-uploading files.',
      cvTitle: 'Reprocess all CV analyses',
      cvDescription: 'Runs sequentially for every user with saved CV combined input.',
      cvAction: 'Reprocess all CVs',
      jobTitle: 'Reprocess all job analyses with CV concordance',
      jobDescription: 'Runs sequentially for every user with saved job combined input and requires a valid CV analysis.',
      jobAction: 'Reprocess all jobs',
      completedSummary: 'Completed for {total} users: {users}'
    },
    systemLanguage: {
      title: 'System Language Management',
      description: 'Configure the language used by the AI system for analysis outputs. This setting affects the language of CV analysis, job analysis, company research, and training recommendation results - not the interface language.',
      selectLanguage: 'Select System Language',
      currentLanguage: 'Current Language',
      currentStatus: 'Current Status',
      lastUpdated: 'Last Updated',
      updatedBy: 'Updated By',
      loading: 'Loading system language settings...',
      saving: 'Saving...',
      english: 'English',
      french: 'Français (French)',
      englishDescription: 'AI analysis outputs will be generated in English',
      frenchDescription: 'AI analysis outputs will be generated in French',
      updateSuccess: 'System language updated successfully to {language}',
      errorLoading: 'Failed to load system language setting',
      errorSaving: 'Failed to save system language setting',
      noChangeNeeded: 'No change needed - language is already set to the selected value'
    }
  },
  [LANGUAGES.FR]: {
    dashboard: 'Tableau de Bord Admin',
    users: 'Utilisateurs',
    roles: 'Rôles',
    settings: 'Paramètres',
    analytics: 'Analytiques',
    logs: 'Journaux Système',
    maintenance: 'Maintenance',
    backup: 'Sauvegarde & Restauration',
    userManagement: 'Gestion des Utilisateurs',
    roleManagement: 'Gestion des Rôles',
    role: 'Rôle',
    department: 'Département',
    departmentLabel: 'Département',
    departmentPlaceholder: 'Sélectionner le Département',
    noDepartment: 'Aucun Département',
    description: 'Description',
    permissions: 'Permissions',
    actions: 'Actions',
    permissionsCount: 'Nombre de Permissions',
    roleLabels: {
      user: 'Utilisateur',
      manager: 'Gestionnaire',
      admin: 'Administrateur'
    },
    createdBy: 'Créé Par',
    createdAt: 'Créé Le',
    used: 'Utilisé',
    unused: 'Disponible',
    available: 'Disponible',
    emailAddress: 'Adresse Courriel',
    type: 'Type',
    createdOn: 'Créé le',
    expiresOn: 'Expire le',
    pending: 'En Attente',
    // Department Management
    departmentManagement: 'Gestion des Départements',

    // Skills Management
    skillManagement: 'Gestion des Compétences',
    skills: {
      title: 'Gestion des Catégories de Compétences',
      description: 'Gérer les catégories de compétences utilisées dans l\'analyseur CV et l\'analyseur d\'emploi. Ces catégories aident à organiser et catégoriser les compétences et habiletés.',
      name: 'Nom',
      descriptionLabel: 'Description',
      type: 'Type',
      order: 'Ordre d\'Affichage',
      status: 'Statut',
      active: 'Actif',
      created: 'Créé',
      noCategories: 'Aucune catégorie de compétences trouvée',
      addCategory: 'Ajouter une Catégorie',
      editCategory: 'Modifier la Catégorie',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer cette catégorie?',
      loadError: 'Échec du chargement des catégories de compétences',
      saveError: 'Échec de la sauvegarde de la catégorie',
      deleteError: 'Échec de la suppression de la catégorie',
      createSuccess: 'Catégorie créée avec succès',
      updateSuccess: 'Catégorie mise à jour avec succès',
      deleteSuccess: 'Catégorie supprimée avec succès',
      typeAll: 'Tous (CV et Emploi)',
      typeCv: 'CV Seulement',
      typeJob: 'Emploi Seulement'
    },

    departments: {
      title: 'Gestion des Départements',
      subtitle: 'Gérer les départements organisationnels et les affectations',
      nameColumn: 'Nom du Département',
      descriptionColumn: 'Description',
      statusColumn: 'Statut',
      usersColumn: 'Utilisateurs',
      createdColumn: 'Créé',
      addDepartment: 'Ajouter un Département',
      editDepartment: 'Modifier le Département',
      deleteDepartment: 'Supprimer le Département',
      nameLabel: 'Nom du Département',
      nameRequired: 'Le nom du département est requis',
      nameAlreadyExists: 'Ce nom de département existe déjà',
      namePlaceholder: 'Entrez le nom du département',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Entrez la description du département (optionnel)',
      createButton: 'Créer le Département',
      updateButton: 'Modifier le Département',
      createSuccess: 'Département créé avec succès',
      updateSuccess: 'Département modifié avec succès',
      deleteSuccess: 'Département supprimé avec succès',
      createError: 'Échec de la création du département',
      updateError: 'Échec de la modification du département',
      deleteError: 'Échec de la suppression du département',
      submitError: 'Une erreur s\'est produite lors du traitement de votre demande',
      confirmDelete: 'Êtes-vous sûr de vouloir supprimer "{name}" ? Cette action ne peut pas être annulée.',
      usersInDepartment: 'utilisateurs dans ce département'
    },
    // System Language Management
    systemLanguageManagement: 'Gestion de la Langue du Système',
    reprocessingManagement: 'Re-traitement',
    reprocessing: {
      title: 'Re-traitement des analyseurs',
      description: 'Relance les entrées combinées enregistrées après changement de prompt, taxonomie ou information entreprise sans re-téléverser les fichiers.',
      cvTitle: 'Re-traiter toutes les analyses CV',
      cvDescription: 'Exécution séquentielle pour chaque utilisateur ayant une entrée CV combinée enregistrée.',
      cvAction: 'Re-traiter tous les CV',
      jobTitle: 'Re-traiter toutes les analyses emploi avec concordance CV',
      jobDescription: 'Exécution séquentielle pour chaque utilisateur ayant une entrée emploi combinée et nécessite une analyse CV valide.',
      jobAction: 'Re-traiter tous les emplois',
      completedSummary: 'Terminé pour {total} utilisateurs : {users}'
    },
    systemLanguage: {
      title: 'Gestion de la Langue du Système',
      description: 'Configurez la langue utilisée par le système IA pour les résultats d\'analyse. Ce paramètre affecte la langue des analyses de CV, d\'emplois, de recherche d\'entreprise et des recommandations de formation - pas la langue de l\'interface.',
      selectLanguage: 'Sélectionner la Langue du Système',
      currentLanguage: 'Langue Actuelle',
      currentStatus: 'État Actuel',
      lastUpdated: 'Dernière Mise à Jour',
      updatedBy: 'Mis à Jour Par',
      loading: 'Chargement des paramètres de langue du système...',
      saving: 'Sauvegarde...',
      english: 'English (Anglais)',
      french: 'Français',
      englishDescription: 'Les résultats d\'analyse IA seront générés en anglais',
      frenchDescription: 'Les résultats d\'analyse IA seront générés en français',
      updateSuccess: 'Langue du système mise à jour avec succès vers {language}',
      errorLoading: 'Échec du chargement du paramètre de langue du système',
      errorSaving: 'Échec de la sauvegarde du paramètre de langue du système',
      noChangeNeeded: 'Aucun changement nécessaire - la langue est déjà définie sur la valeur sélectionnée'
    }
  }
};
